import { useMutation } from '@apollo/client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Accept } from 'react-dropzone';
import BucketUpload from './BucketUpload';
import * as styles from './ImportFileModal.module.scss';
import ImportFileProgress from './ImportFileProgress';
import ImportFilesConfirmation from './ImportFilesConfirmation';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Modal, { ModalTitle } from '../../components/Modal';
import Uploader from '../../components/Uploader';
import { gql } from '../../graphql/generated/gql';
import type { ImportExtension } from '../../graphql/generated/graphql';
import { ImportSource } from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import Logger from '../../utils/Logger';
import getExtname from '../../utils/url/getExtname';
import useBeforeLeaveConfirm from '../../utils/useBeforeLeaveConfirm';

const IMPORT_FILES_MUTATION = gql(/** @complexity 8 */ `
  mutation importFiles($files: [Upload]!, $source: ImportSource) {
    importedFiles: importFiles(files: $files, source: $source) {
      ...ImportFilesConfirmation_importedFiles
    }
  }
`);

const GENERATE_IMPORT_UPLOAD_URL_MUTATION = gql(/** @complexity 2 */ `
  mutation generateImportUploadUrl(
    $extension: ImportExtension!
    $source: ImportSource!
  ) {
    url: generateImportUploadUrl(extension: $extension, source: $source)
  }
`);

const COMPLETE_IMPORT_MUTATION = gql(/** @complexity 2 */ `
  mutation completeUploadedImport($url: String!, $byteSize: Int!) {
    completeUploadedImport(url: $url, byteSize: $byteSize)
  }
`);

interface ImportFileModalProps {
  accept?: Accept;
  close: () => void;
  footer?: React.ReactNode;
  forceBucketUpload?: boolean;
  offlineProcessingTimeout?: number;
  source?: ImportSource;
  uploaderSubject?: string;
}

const BACKEND_TIMEOUT_FOR_OFFLINE_PROCESSING = 20 * 1000;
const BUCKET_UPLOAD_MIN_SIZE = 25 * 1000 * 1000;

const useDirectUpload = ({
  offlineProcessingTimeout,
  onError,
}: {
  offlineProcessingTimeout: number;
  onError: () => unknown;
}) => {
  const [importFiles, { data, loading }] = useMutation(IMPORT_FILES_MUTATION);
  const [isOfflineProcessing, setIsOfflineProcessing] = useState(false);
  const fileSizeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (loading) {
      setIsOfflineProcessing(false);

      const timeout = setTimeout(() => {
        // If there is truly nothing to import, the backend should respond quickly
        // If we are waiting for a long time, it likely means backend is switching to
        // offline processing
        setIsOfflineProcessing(true);
        Logger.message('Detected offline import processing', {
          size: fileSizeRef.current,
        });
      }, offlineProcessingTimeout);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [loading, offlineProcessingTimeout]);

  const handleUpload = useCallback(
    async (file: File, source: ImportSource) => {
      try {
        fileSizeRef.current = file.size;
        await importFiles({ variables: { files: [file], source } });
      } catch (e) {
        setIsOfflineProcessing(false);
        onError();
      }
    },
    [importFiles, onError],
  );

  const result = isOfflineProcessing ? 'OFFLINE' : data?.importedFiles;

  return useMemo(() => [handleUpload, result] as const, [handleUpload, result]);
};

const useBucketUpload = ({ onError }: { onError: () => unknown }) => {
  const [progress, setProgress] = useState<number>(0);
  const [generateImportUploadUrl] = useMutation(
    GENERATE_IMPORT_UPLOAD_URL_MUTATION,
  );
  const [completeImport] = useMutation(COMPLETE_IMPORT_MUTATION);
  const uploadRef = useRef<BucketUpload | undefined>();

  useBeforeLeaveConfirm({ skip: progress <= 0 || progress >= 100 });

  const handleError = useCallback(() => {
    setProgress(0);
    uploadRef.current = undefined;
    onError();
  }, [onError]);

  const prepareUpload = useCallback(
    async (file: File, source: ImportSource) => {
      const extension = getExtname(file.name).toUpperCase() as ImportExtension;
      try {
        const { data } = await generateImportUploadUrl({
          variables: { extension, source },
        });

        return (
          data?.url &&
          new BucketUpload({
            chunkSize: 5 * 1024 * 1024, // 5MB
            file,
            onProgress: ({ loaded, total }) => {
              // Leave 1% for the complete upload call
              setProgress(Math.ceil((loaded / total) * 99));
            },
            url: data.url,
          })
        );
      } catch (e) {
        handleError();
        return undefined;
      }
    },
    [generateImportUploadUrl, handleError],
  );

  const performUpload = useCallback(
    async (upload: BucketUpload) => {
      try {
        await upload.start();
        await completeImport({
          variables: { url: upload.getUrl(), byteSize: upload.getFileSize() },
        });
        setProgress(100);
      } catch (error) {
        Logger.error(error);
        handleError();
      }
    },
    [completeImport, handleError],
  );

  useEffect(() => {
    uploadRef.current?.cancel();
  }, []);

  const bucketUpload = useCallback(
    async (file: File, source: ImportSource) => {
      uploadRef.current?.cancel();
      const upload = await prepareUpload(file, source);

      if (upload) {
        uploadRef.current = upload;
        performUpload(upload);
      }
    },
    [performUpload, prepareUpload],
  );

  const result = useMemo(
    () => [bucketUpload, uploadRef.current ? { progress } : undefined] as const,
    [bucketUpload, progress],
  );

  return result;
};

const DEFAULT_ACCEPT = {
  'application/zip': ['.zip'],
};

const ImportFileModal = ({
  accept = DEFAULT_ACCEPT,
  close,
  footer,
  forceBucketUpload,
  offlineProcessingTimeout = BACKEND_TIMEOUT_FOR_OFFLINE_PROCESSING,
  source = ImportSource.File,
  uploaderSubject = 'a zip file containing your HTML and Markdown files',
}: ImportFileModalProps) => {
  const dispatch = useAppDispatch();
  const handleError = useCallback(() => {
    dispatch(
      UIActions.pushToast({
        level: 'error',
        title: 'Unable to import your data',
        message: 'An error occurred while uploading. Please try again later.',
      }),
    );
  }, [dispatch]);

  const [directUpload, directUploadData] = useDirectUpload({
    offlineProcessingTimeout,
    onError: handleError,
  });
  const [bucketUpload, bucketUploadData] = useBucketUpload({
    onError: handleError,
  });

  const handleDrop = useCallback(
    async ([file]: File[]) => {
      if (!file) return;

      if (file.size > BUCKET_UPLOAD_MIN_SIZE || forceBucketUpload) {
        await bucketUpload(file, source);
      } else {
        await directUpload(file, source);
      }
    },
    [bucketUpload, directUpload, forceBucketUpload, source],
  );

  let content = (
    <>
      <Uploader
        accept={accept}
        image={false}
        maxSizeMB={10 * 1000 * 1000}
        onDrop={handleDrop}
        subject={uploaderSubject}
      />
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </>
  );

  if (bucketUploadData) {
    const { progress } = bucketUploadData;
    content =
      progress >= 100 ? (
        <>
          Your file has been successfully uploaded. We&apos;ll continue the
          import process in the background, and send you an email once it&apos;s
          complete. Meanwhile, feel free to close this modal.
        </>
      ) : (
        <ImportFileProgress message="Uploading…" progress={progress} />
      );
  } else if (directUploadData) {
    content =
      directUploadData === 'OFFLINE' ? (
        <>
          Large import detected. We&apos;ll continue the import process in the
          background. Feel free to close this modal and return to Slab later.
        </>
      ) : (
        <ImportFilesConfirmation importedFiles={directUploadData} />
      );
  }

  return (
    <Modal width={425}>
      <ModalTitle close={close}>Import Files</ModalTitle>
      {content}
    </Modal>
  );
};

export default ImportFileModal;
